import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import {
  getOrganizationUsers,
  revokeUserOrgAccess,
} from "./utils/userManagementApi";
import logger from "./utils/logger";
import SelfRevokeConfirmModal from "./SelfRevokeConfirmModal";
import UserSelectorModal from "./UserSelectorModal";
import "./OrgUsersModal.css";
import "./AddUserModal.css"; // Reuse button styles

const OrgUsersModal = ({ organization, onClose, onUserRevoked }) => {
  const { getAccessToken, user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState(null);

  // For revoke confirmation
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [typedEmail, setTypedEmail] = useState("");
  const [revoking, setRevoking] = useState(false);

  // For self-revocation
  const [showSelfRevokeModal, setShowSelfRevokeModal] = useState(false);
  const selectedOrgId = localStorage.getItem("selectedOrgId");

  // For user authorization
  const [showUserSelector, setShowUserSelector] = useState(false);

  // Role checks
  const isFullAdmin = currentUser?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === "Full Admin"
  );
  const isOrgAdmin = currentUser?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === "Organization Admin"
  );
  const canRevokeAccess = isFullAdmin || isOrgAdmin;

  useEffect(() => {
    loadOrganizationUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading && !showRevokeConfirm) {
        e.stopPropagation(); // Prevent event from bubbling to parent modals
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, loading, showRevokeConfirm]);

  const loadOrganizationUsers = async () => {
    try {
      setLoading(true);
      setMessage(null);
      logger.log(
        "[OrgUsersModal] Loading users for organization:",
        organization.orgId
      );

      const result = await getOrganizationUsers(
        organization.orgId,
        getAccessToken
      );

      logger.log(
        "[OrgUsersModal] Received users:",
        result.count,
        "users for org",
        result.orgId
      );
      setUsers(result.users || []);
    } catch (error) {
      logger.error("[OrgUsersModal] Failed to load organization users:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load organization users",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeClick = (user) => {
    // Check if current user can revoke from this user based on roles
    const targetIsFullAdmin = user.role === "Full Admin";
    const targetIsOrgAdmin = user.role === "Organization Admin";
    const targetIsSiteAdmin = user.role === "Site Admin";
    const isTargetCurrentUser = user.userId === currentUser?.userId;

    // Full Admins can revoke their own access, but not other Full Admins
    if (targetIsFullAdmin && !isTargetCurrentUser) {
      setMessage({
        type: "error",
        text: "Cannot revoke access from other Full Admin users",
      });
      return;
    }

    // Check if this is self-revocation (use currentUser.userId which we already compared above)
    if (isTargetCurrentUser) {
      // Self-revocation flow - show special confirmation modal
      setSelectedUser(user);
      setShowSelfRevokeModal(true);
      return;
    }

    if (isFullAdmin) {
      // Full Admins can revoke from Org Admins, Site Admins, and themselves
      setSelectedUser(user);
      setTypedEmail("");
      setShowRevokeConfirm(true);
    } else if (isOrgAdmin) {
      // Org Admins can revoke from Org Admins and Site Admins
      if (targetIsOrgAdmin || targetIsSiteAdmin) {
        setSelectedUser(user);
        setTypedEmail("");
        setShowRevokeConfirm(true);
      } else {
        setMessage({
          type: "error",
          text: "Organization Admins can only revoke access from Organization Admins and Site Admins",
        });
      }
    } else {
      setMessage({
        type: "error",
        text: "You do not have permission to revoke organization access",
      });
    }
  };

  const handleRevokeConfirm = async () => {
    // Validate typed text matches expected confirmation
    const expectedText = selectedUser.email
      ? selectedUser.email.toLowerCase()
      : "delete";
    if (typedEmail.trim().toLowerCase() !== expectedText) {
      setMessage({
        type: "error",
        text: selectedUser.email
          ? "Email does not match. Please type the user's email exactly."
          : "Please type 'delete' to confirm.",
      });
      return;
    }

    try {
      setRevoking(true);
      setMessage(null);

      // Extract Auth0 ID from federatedUserId if needed
      // Format: oidc|FederationName|auth0|123456 -> auth0|123456
      let userIdToRevoke = selectedUser.userId;
      if (!userIdToRevoke && selectedUser.federatedUserId) {
        const fedId = selectedUser.federatedUserId;
        // Check if it's the OIDC federation format
        if (fedId.startsWith("oidc|")) {
          const parts = fedId.split("|");
          if (parts.length >= 4) {
            // Last two parts are the Auth0 provider and ID
            userIdToRevoke = `${parts[parts.length - 2]}|${
              parts[parts.length - 1]
            }`;
          } else {
            userIdToRevoke = fedId;
          }
        } else {
          userIdToRevoke = fedId;
        }
      }

      logger.log(
        "[OrgUsersModal] Revoking access for user:",
        userIdToRevoke,
        "(original:",
        selectedUser.userId || selectedUser.federatedUserId,
        ") from org:",
        organization.orgId
      );

      await revokeUserOrgAccess(
        userIdToRevoke,
        organization.orgId,
        getAccessToken
      );

      const displayName =
        selectedUser.email ||
        (selectedUser.firstName && selectedUser.lastName
          ? `${selectedUser.firstName} ${selectedUser.lastName}`
          : selectedUser.federatedUserId);

      setMessage({
        type: "success",
        text: `Successfully revoked access for ${displayName}`,
      });

      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);

      // Remove user from list
      const userIdToRemove =
        selectedUser.userId || selectedUser.federatedUserId;
      setUsers((prev) =>
        prev.filter((u) => (u.userId || u.federatedUserId) !== userIdToRemove)
      );

      // Close confirmation modal
      setShowRevokeConfirm(false);
      setSelectedUser(null);
      setTypedEmail("");

      // Notify parent component
      if (onUserRevoked) {
        onUserRevoked();
      }

      // Clear localStorage cache to force refresh
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");
    } catch (error) {
      logger.error("[OrgUsersModal] Failed to revoke access:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to revoke organization access",
      });
    } finally {
      setRevoking(false);
    }
  };

  const handleCancelRevoke = () => {
    setShowRevokeConfirm(false);
    setSelectedUser(null);
    setTypedEmail("");
    setMessage(null);
  };

  const handleConfirmSelfRevoke = async () => {
    if (!selectedUser) return;

    try {
      setRevoking(true);
      setMessage(null);

      const isCurrentOrg = organization.orgId === selectedOrgId;

      logger.log(
        "[OrgUsersModal] Self-revoking access for user:",
        selectedUser.userId,
        "from org:",
        organization.orgId,
        "isCurrentOrg:",
        isCurrentOrg
      );

      await revokeUserOrgAccess(
        selectedUser.userId,
        organization.orgId,
        getAccessToken
      );

      // Clear localStorage cache
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");

      // If revoking current org, clear selection and close modal
      if (isCurrentOrg) {
        localStorage.removeItem("selectedOrgId");
        localStorage.removeItem("selectedInstallationId");
        logger.log(
          "[OrgUsersModal] Self-revoked current org, cleared localStorage"
        );

        // Dispatch custom event to notify OrgInstallWrapper (storage event doesn't fire in same tab)
        window.dispatchEvent(new CustomEvent("selfRevokedCurrentOrg"));

        // Close modals - DO NOT call onUserRevoked() as it triggers refresh that interferes with redirect
        setShowSelfRevokeModal(false);
        setSelectedUser(null);
        onClose();
      } else {
        // Just refresh the user list
        setMessage({
          type: "success",
          text: `Successfully revoked your access from ${
            organization.name || organization.orgId
          }`,
        });
        setUsers((prev) =>
          prev.filter((u) => u.userId !== selectedUser.userId)
        );
        setShowSelfRevokeModal(false);
        setSelectedUser(null);

        if (onUserRevoked) {
          onUserRevoked();
        }
      }
    } catch (error) {
      logger.error("[OrgUsersModal] Failed to self-revoke access:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to revoke organization access",
      });
    } finally {
      setRevoking(false);
    }
  };

  const handleUserGranted = () => {
    // Refresh the user list to show the newly granted user
    loadOrganizationUsers();
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const fullName = `${user.firstName || ""} ${
      user.lastName || ""
    }`.toLowerCase();
    return (
      fullName.includes(search) ||
      (user.email || "").toLowerCase().includes(search) ||
      (user.role || "").toLowerCase().includes(search)
    );
  });

  const getRoleDisplayName = (role) => {
    if (role === "Full Admin") return "Full Admin";
    if (role === "Organization Admin") return "Org Admin";
    return role;
  };

  const canRevokeFromUser = (targetUser) => {
    const targetIsFullAdmin = targetUser.role === "Full Admin";
    const targetIsOrgAdmin = targetUser.role === "Organization Admin";
    const targetIsSiteAdmin = targetUser.role === "Site Admin";
    const isTargetCurrentUser = targetUser.userId === currentUser?.userId;

    // Full Admins can revoke their own access, but not other Full Admins
    if (targetIsFullAdmin && !isTargetCurrentUser) {
      return false;
    }

    if (isFullAdmin) {
      return true; // Full Admins can revoke from Org Admins, Site Admins, and themselves
    }
    if (isOrgAdmin) {
      // Org Admins can revoke from Org Admins and Site Admins
      return targetIsOrgAdmin || targetIsSiteAdmin;
    }
    return false;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content org-users-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            👥 Users for {organization.name || organization.organizationName}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {message && (
          <div className={`modal-message ${message.type}`}>{message.text}</div>
        )}

        {!loading && (
          <>
            <div className="org-users-search">
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="org-users-authorize-section">
              <button
                className="btn-authorize-users"
                onClick={() => setShowUserSelector(true)}
                title="Authorize users for this organization"
              >
                + Authorize Users
              </button>
            </div>
          </>
        )}

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              {users.length === 0
                ? "No users authorized for this organization"
                : "No users match your search"}
            </div>
          ) : (
            <div className="org-users-list">
              {filteredUsers.map((user) => (
                <div
                  key={user.userId || user.federatedUserId}
                  className="org-user-item"
                >
                  <div className="org-user-info">
                    <div className="org-user-name">
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email || user.federatedUserId}
                    </div>
                    <div className="org-user-email">
                      {user.email || "No email available"}
                    </div>
                    <div className="org-user-role">
                      {getRoleDisplayName(user.role)}
                    </div>
                  </div>
                  {canRevokeAccess && canRevokeFromUser(user) && (
                    <button
                      className="btn-revoke-user"
                      onClick={() => handleRevokeClick(user)}
                      title={`Revoke access for ${user.email}`}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            data-theme-debug="true"
            style={{
              backgroundColor: document.body.classList.contains("light-mode")
                ? "#e0e0e0"
                : "#3d3d3d",
              color: document.body.classList.contains("light-mode")
                ? "#333"
                : "#f0f0f0",
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Revoke Confirmation Modal */}
      {showRevokeConfirm && selectedUser && (
        <div className="modal-overlay" onClick={handleCancelRevoke}>
          <div
            className="modal-content confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Confirm Revoke Access</h3>
            </div>
            <div className="modal-body">
              <p
                style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
              >
                Are you sure you want to revoke organization access for{" "}
                <strong>
                  {selectedUser.firstName && selectedUser.lastName
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : selectedUser.email || selectedUser.federatedUserId}
                </strong>
                {selectedUser.email && ` (${selectedUser.email})`}?
              </p>
              {selectedUser.email ? (
                <>
                  <p className="warning-text">
                    To confirm, please type the user's email address:
                  </p>
                  <p className="warning-text">{selectedUser.email}</p>
                  <input
                    type="email"
                    placeholder={selectedUser.email}
                    value={typedEmail}
                    onChange={(e) => setTypedEmail(e.target.value)}
                    className="email-confirm-input"
                    autoFocus
                  />
                </>
              ) : (
                <>
                  <p
                    className="warning-text"
                    style={{ display: "inline-block", width: "100%" }}
                  >
                    To confirm, please type the word{" "}
                    <span
                      style={{
                        color: "#ff6b6b",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      delete
                    </span>
                    :
                  </p>
                  <input
                    type="text"
                    placeholder="delete"
                    value={typedEmail}
                    onChange={(e) => setTypedEmail(e.target.value)}
                    className="email-confirm-input"
                    autoFocus
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCancelRevoke}
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleRevokeConfirm}
                disabled={
                  revoking ||
                  (selectedUser.email
                    ? typedEmail.trim().toLowerCase() !==
                      selectedUser.email.toLowerCase()
                    : typedEmail.trim().toLowerCase() !== "delete")
                }
              >
                {revoking ? "Revoking..." : "Revoke Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-Revocation Confirmation Modal */}
      <SelfRevokeConfirmModal
        isOpen={showSelfRevokeModal}
        onClose={() => {
          setShowSelfRevokeModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmSelfRevoke}
        orgName={organization.name || organization.orgId}
        isCurrentOrg={organization.orgId === selectedOrgId}
        loading={revoking}
        userEmail={currentUser?.email}
      />

      {/* User Selector Modal */}
      <UserSelectorModal
        isOpen={showUserSelector}
        onClose={() => setShowUserSelector(false)}
        organization={organization}
        authorizedUserIds={new Set(users.map((u) => u.userId))}
        onUserGranted={handleUserGranted}
      />
    </div>
  );
};

export default OrgUsersModal;
